import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Palette, Download, Share2, Loader2, X, RefreshCw, Scissors, Shirt, Package, BookOpen, Calendar as CalendarIcon, PenTool, Upload, ImageIcon, CheckCircle2 } from "lucide-react";

/**
 * Admin · Corporate Identity Vault.
 *
 * Digital studio for the Vinci brand: obsidian gallery of assets, editable
 * fields for stationery, per-asset PNG preview + print-ready PDF export.
 */

const CATEGORY_META = {
  core:      { label: "Core Stationery",     accent: "#D8B23E", blurb: "Print-ready stationery — CMYK, crop-marked." },
  marketing: { label: "Marketing & Events",  accent: "#2FE4C6", blurb: "Trade-show + campaign collateral." },
  swag:      { label: "Lifestyle & Swag",    accent: "#B0B0B0", blurb: "Placement mockups for apparel + merch." },
};

const ICON_FOR = {
  visiting_card: Scissors, letterhead: Palette, envelope: Package,
  standee: Package, brochure: BookOpen, catalogue_cover: BookOpen, wall_calendar: CalendarIcon,
  tshirt: Shirt, shirt: Shirt, cap: Shirt, pen: PenTool, diary: BookOpen,
};

export function AdminBrandAssets({ api }) {
  const [assets, setAssets] = useState([]);
  const [logo, setLogo] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openAsset, setOpenAsset] = useState(null); // asset definition
  const [config, setConfig] = useState({});
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/brand-assets");
      setAssets(r.data.assets || []);
      setLogo(r.data.logo || null);
    } catch { toast.error("Could not load brand vault"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Logo must be ≤ 4 MB"); return; }
    setLogoBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/brand-assets/logo", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setLogo(r.data.logo);
      toast.success("Master logo updated · every asset will use it");
      // force a preview refresh if a studio is open
      if (openAsset) generatePreview(openAsset, config);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setLogoBusy(false); }
  };

  const grouped = useMemo(() => {
    const g = { core: [], marketing: [], swag: [] };
    assets.forEach(a => { (g[a.category] = g[a.category] || []).push(a); });
    return g;
  }, [assets]);

  const openStudio = (asset) => {
    setOpenAsset(asset);
    const initial = {};
    (asset.editable || []).forEach(f => { initial[f.key] = f.default; });
    setConfig(initial);
    setPreviewUrl("");
    // eagerly generate the first preview
    setTimeout(() => generatePreview(asset, initial), 60);
  };

  const closeStudio = () => {
    setOpenAsset(null);
    if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setConfig({});
  };

  const generatePreview = async (asset = openAsset, cfg = config) => {
    if (!asset) return;
    setPreviewLoading(true);
    try {
      const r = await api.post(`/admin/brand-assets/${asset.id}/preview`,
                               { config: cfg }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setPreviewUrl(prev => {
        if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setPreviewLoading(false); }
  };

  const downloadPdf = async () => {
    if (!openAsset) return;
    setPdfLoading(true);
    try {
      const r = await api.post(`/admin/brand-assets/${openAsset.id}/pdf`,
                               { config }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vinci-${openAsset.id}-print-ready.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("PDF downloaded");
    } catch (e) { toast.error(e.response?.data?.detail || "PDF failed"); }
    finally { setPdfLoading(false); }
  };

  const shareSoftCopy = async () => {
    if (!previewUrl) { await generatePreview(); return; }
    try {
      const resp = await fetch(previewUrl);
      const blob = await resp.blob();
      const file = new File([blob], `vinci-${openAsset.id}-softcopy.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Vinci · ${openAsset.title}` });
      } else {
        const a = document.createElement("a");
        a.href = previewUrl; a.download = file.name;
        document.body.appendChild(a); a.click(); a.remove();
        toast.success("Soft copy downloaded (share sheet unsupported on this browser)");
      }
    } catch { toast.error("Could not share"); }
  };

  const updateField = (key, val) => {
    setConfig(prev => ({ ...prev, [key]: val }));
  };

  return (
    <div data-testid="admin-brand-assets" className="space-y-8">
      <div className="flex items-start gap-4 border-b border-white/10 pb-6">
        <div className="h-12 w-12 rounded-sm bg-[#D8B23E]/15 text-[#D8B23E] flex items-center justify-center shrink-0">
          <Palette size={20}/>
        </div>
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">
            Corporate Identity Vault
          </div>
          <h2 className="font-serif text-3xl text-[#F5F5F0] mt-1">The Vinci Digital Studio.</h2>
          <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl">
            Every letterhead, card, standee and swag mockup in one gallery. Edit the details,
            preview, and ship a print-ready PDF straight to your prepress house. Obsidian · Brushed
            Gold · Steel Silver — always understated, always unmistakable.
          </p>
        </div>
      </div>

      {loading && <div className="text-[#8A8A82] font-body">Loading vault…</div>}

      {/* Master Vinci logo upload */}
      <div data-testid="brand-logo-manager"
           className="border border-[#D8B23E]/30 rounded-sm bg-gradient-to-br from-[#D8B23E]/[0.06] to-[#B87333]/[0.02] p-5 flex flex-wrap items-center gap-5">
        <div className="h-24 w-24 border border-white/10 rounded-sm bg-black/60 grid place-items-center overflow-hidden shrink-0">
          {logo ? (
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/brand-assets/logo?v=${logo.uploaded_at}`}
                 alt="Vinci master logo" className="max-h-full max-w-full object-contain"
                 data-testid="brand-logo-preview"/>
          ) : (
            <ImageIcon size={26} className="text-[#8A8A82]"/>
          )}
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Master Vinci logo</div>
          <div className="font-serif text-xl mt-1">Used across every stationery + swag piece</div>
          <div className="font-body text-[12px] text-[#8A8A82] mt-1">
            PNG / JPEG / WebP with transparent background · ≤ 4 MB · square or wide.
            {logo ? (
              <> Uploaded {new Date(logo.uploaded_at).toLocaleString("en-IN")} by {logo.uploaded_by}. Square-crop {logo.size?.[0]}×{logo.size?.[1]}.</>
            ) : <> Not yet uploaded — assets fall back to the geometric V-shield.</>}
          </div>
        </div>
        <label data-testid="brand-logo-upload"
               className="cursor-pointer inline-flex items-center gap-2 rounded-sm border border-[#D8B23E] text-[#D8B23E] hover:bg-[#D8B23E]/10 px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em]">
          {logoBusy ? <Loader2 size={12} className="animate-spin"/> :
                      logo ? <CheckCircle2 size={12}/> : <Upload size={12}/>}
          {logo ? "Replace logo" : "Upload master logo"}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                 onChange={e => uploadLogo(e.target.files?.[0])}/>
        </label>
      </div>

      {["core", "marketing", "swag"].map((cat) => (
        (grouped[cat] || []).length > 0 && (
          <section key={cat}>
            <div className="flex items-baseline gap-3 mb-4">
              <h3 className="font-serif text-2xl text-[#F5F5F0]">{CATEGORY_META[cat].label}</h3>
              <span className="font-grotesk text-[10px] uppercase tracking-[0.28em]"
                    style={{ color: CATEGORY_META[cat].accent }}>
                {(grouped[cat] || []).length} pieces
              </span>
              <span className="font-body text-xs text-[#8A8A82]">· {CATEGORY_META[cat].blurb}</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(grouped[cat] || []).map((a) => {
                const Icon = ICON_FOR[a.id] || Palette;
                return (
                  <motion.button
                    key={a.id}
                    data-testid={`brand-asset-card-${a.id}`}
                    onClick={() => openStudio(a)}
                    whileHover={{ y: -3, borderColor: CATEGORY_META[cat].accent }}
                    className="text-left border border-white/10 rounded-sm bg-[#0a0a0a] p-5 relative overflow-hidden group">
                    <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-[0.06]"
                         style={{ background: `radial-gradient(circle, ${CATEGORY_META[cat].accent} 0%, transparent 70%)` }}/>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-sm bg-black/50 flex items-center justify-center"
                           style={{ color: CATEGORY_META[cat].accent }}>
                        <Icon size={16}/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-serif text-lg text-[#F5F5F0]">{a.title}</div>
                        <div className="font-body text-[11px] text-[#8A8A82]">
                          {a.size_mm[0]}×{a.size_mm[1]} mm{a.bleed_mm ? ` · ${a.bleed_mm} mm bleed` : ""}
                        </div>
                      </div>
                    </div>
                    <p className="font-body text-[12px] text-[#8A8A82] mt-3 leading-relaxed">{a.blurb}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="font-grotesk text-[9px] uppercase tracking-[0.28em]"
                            style={{ color: CATEGORY_META[cat].accent }}>Open studio</span>
                      <span className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#6f6f68]">
                        {(a.editable || []).length ? `${(a.editable || []).length} fields` : "Static"}
                      </span>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </section>
        )
      ))}

      {/* Studio drawer */}
      <AnimatePresence>
        {openAsset && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-stretch justify-end p-0 sm:p-4"
            onClick={(e) => { if (e.target === e.currentTarget) closeStudio(); }}>
            <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }}
              className="bg-[#050506] w-full max-w-6xl border-l border-[#D8B23E]/30 h-full overflow-y-auto"
              data-testid="brand-asset-studio">
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-[#050506]/95 backdrop-blur px-6 py-4">
                <div className="min-w-0">
                  <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">
                    {CATEGORY_META[openAsset.category]?.label}
                  </div>
                  <div className="font-serif text-2xl text-[#F5F5F0] truncate">{openAsset.title}</div>
                  <div className="font-body text-[11px] text-[#8A8A82] mt-1">{openAsset.blurb}</div>
                </div>
                <button data-testid="brand-asset-close" onClick={closeStudio} className="text-[#8A8A82] hover:text-[#F5F5F0]"><X size={20}/></button>
              </div>

              <div className="grid lg:grid-cols-2 gap-6 p-6">
                {/* Preview pane */}
                <div className="space-y-3">
                  <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#8A8A82] flex items-center justify-between">
                    Live preview
                    <button onClick={() => generatePreview()} className="text-[#2FE4C6] hover:text-[#F5F5F0]"
                            data-testid="brand-asset-refresh-preview">
                      {previewLoading ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>}
                    </button>
                  </div>
                  <div className="relative border border-white/10 rounded-sm bg-[#0a0a0a] p-4 flex items-center justify-center min-h-[320px]">
                    {previewLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
                        <Loader2 size={20} className="animate-spin text-[#D8B23E]"/>
                      </div>
                    )}
                    {previewUrl ? (
                      <img data-testid="brand-asset-preview-img"
                           src={previewUrl} alt={`${openAsset.title} preview`}
                           className="max-w-full max-h-[560px] object-contain shadow-2xl"/>
                    ) : (
                      <div className="text-[#8A8A82] font-body text-sm">Preview will render here…</div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 pt-2">
                    <button data-testid="brand-asset-download-pdf"
                            onClick={downloadPdf} disabled={pdfLoading}
                            className="bg-[#D8B23E] hover:bg-[#c5a233] disabled:opacity-40 text-black font-grotesk text-[10px] uppercase tracking-[0.24em] px-4 py-2.5 rounded-sm inline-flex items-center gap-2">
                      {pdfLoading ? <Loader2 size={12} className="animate-spin"/> : <Download size={12}/>}
                      Download PDF · Print-ready
                    </button>
                    <button data-testid="brand-asset-share-softcopy"
                            onClick={shareSoftCopy}
                            className="border border-[#2FE4C6]/40 text-[#2FE4C6] hover:bg-[#2FE4C6]/10 font-grotesk text-[10px] uppercase tracking-[0.24em] px-4 py-2.5 rounded-sm inline-flex items-center gap-2">
                      <Share2 size={12}/> Share soft copy
                    </button>
                  </div>
                </div>

                {/* Editable fields pane */}
                <div className="space-y-4">
                  <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#8A8A82]">
                    Editable fields · {(openAsset.editable || []).length}
                  </div>
                  {(openAsset.editable || []).length === 0 && (
                    <div className="font-body text-[12px] text-[#6f6f68] italic border border-dashed border-white/10 rounded-sm p-4">
                      This asset has no editable fields — hit "Download PDF" to grab the master file.
                    </div>
                  )}
                  <div className="grid gap-3">
                    {(openAsset.editable || []).map((f) => (
                      <div key={f.key}>
                        <div className="font-body text-[11px] text-[#8A8A82] mb-1">{f.label}</div>
                        <input data-testid={`brand-asset-field-${f.key}`}
                               type="text" value={config[f.key] || ""}
                               onChange={(e) => updateField(f.key, e.target.value)}
                               placeholder={f.default}
                               className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] font-body focus:outline-none focus:border-[#D8B23E]"/>
                      </div>
                    ))}
                  </div>
                  {(openAsset.editable || []).length > 0 && (
                    <button onClick={() => generatePreview()}
                            data-testid="brand-asset-apply-changes"
                            className="border border-[#D8B23E]/50 text-[#D8B23E] hover:bg-[#D8B23E]/10 font-grotesk text-[10px] uppercase tracking-[0.24em] px-4 py-2 rounded-sm inline-flex items-center gap-2">
                      <RefreshCw size={12}/> Apply changes
                    </button>
                  )}
                  <div className="border-t border-white/10 pt-4 space-y-1 font-body text-[11px] text-[#6f6f68]">
                    <div>Trim size · <span className="text-[#F5F5F0]">{openAsset.size_mm[0]} × {openAsset.size_mm[1]} mm</span></div>
                    {openAsset.bleed_mm > 0 && <div>Bleed · <span className="text-[#F5F5F0]">{openAsset.bleed_mm} mm</span></div>}
                    <div>Colour intent · <span className="text-[#F5F5F0]">CMYK-ready (prepress the PNG at your printer)</span></div>
                    <div>Master brand language · <span className="text-[#D8B23E]">Obsidian · Brushed Gold · Steel Silver</span></div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
